/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
// libraries
import { useDebounce } from 'use-lodash-debounce';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Chip,
    TextField,
    Autocomplete,
} from '@mui/material';
import { isEmpty } from 'lodash';
// redux
import { useSelector, dispatch } from '../../redux/store';
import { updateCounter } from '../../redux/slices/lead';
import { getAllTagItem, startLoading } from '../../redux/slices/tagsCreate';
// validation
import { AutocompleteTagsSchema } from '../../validations/validations';
// util
import {
    TabCount,
    TablePageNumber,
    TablePerPage,
    TableSearch,
    TableTreatment,
    TableFilterDate,
    TableStatus,
    TableFilterTags,
    TableTags,
    TableOrder,
    TableOrderBy,
    UpdateTableData,
    TableReferral,
    TableDepositPending,
    TableCategory,
    TableBookedWith
} from '../../utils/allTableTab';
// style
import { grey } from '../../theme/palette';
import {
    tagsBoxStyle,
    searchTagsStyle,
    InputTags
} from '../../styles/AutocompleteTags';
import { filterDropdownPaperStyle } from '../../styles/NewLead';

const SearchAutocompleteTags = ({ tableType, reloadCounter, updateLeadPage, handlePage, handleStartedTablePayload }) => {
    const listElem = useRef();
    const mounted = useRef();
    const [position, setPosition] = useState(0);

    const {
        dashContact: { tag: dashTag, filterTags: dashFilterTags },
        newContact: { tag: newTag, filterTags: newFilterTags },
        attendContact: { tag: attendedTag, filterTags: attendedFilterTags },
        clincheckContact: { tag: clincheckAttendedTag, filterTags: clincheckAttendedFilterTags },
        bookedContact: { tag: bookedTag, filterTags: bookedFilterTags },
        treatmentStarted: { tag: treatmentTag, filterTags: treatmentFilterTags },
        dashNewContact: { tag: dashNewTag, filterTags: dashNewFilterTags },
        archivedContact: { tag: archivedContactTags, filterTags: archiveFilterTags },
        attendedChecklistsContact: { tag: attendedChecklistsTag, filterTags: attendedChecklistsFilterTags },
    } = useSelector((state) => state.lead);

    const contactCount = TabCount(tableType);
    const pageNumber = TablePageNumber(tableType);
    const perPage = TablePerPage(tableType);
    const searchValue = TableSearch(tableType);
    const treatmentValue = TableTreatment(tableType);
    const searchFilterDate = TableFilterDate(tableType);
    const status = TableStatus(tableType);
    const searchTags = TableTags(tableType);
    const searchTagsValue = TableFilterTags(tableType);
    const tableOrder = TableOrder(tableType);
    const tableOrderBy = TableOrderBy(tableType);
    const referral = TableReferral(tableType);
    // Must read the deposit filter (not referral) so a tag-triggered reload preserves
    // an active Deposit filter. Mirrors NewLead.jsx's own TableDepositPending(type) read.
    const depositPendingStatus = TableDepositPending(tableType);
    const category = TableCategory(tableType);
    const bookedWith = TableBookedWith(tableType);
    const [inputValue, setInputValue] = useState('');
    const inputValueDebounce = useDebounce(inputValue, 800);

    const needToCall = useRef(false);

    const { getAllTag, isLoading } = useSelector((state) => state?.tagsCreate || {});
    const { results = [], count = 0 } = getAllTag || {};
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);

    const treatmentData = [];
    treatmentValue?.map((value) => treatmentData.push(value.id));

    const startDate = searchFilterDate ? searchFilterDate[0]?.startDate : null;
    const endDate = searchFilterDate ? searchFilterDate[0]?.endDate : null;
    const [tagsOptions, setTagsOptions] = useState([]);
    const [shouldCall, setShouldCall] = useState(false);

    const NO_TAGS_OPTION = { id: -1, name: 'No Tags', isNoTags: true };

    useEffect(() => {
        if (results) {
            setTagsOptions([NO_TAGS_OPTION, ...results]);
        }
    }, [results]);

    useEffect(() => {
        if (searchTagsValue && shouldCall) {
            setShouldCall(false);
            dispatch(updateCounter([]));
            const reloadPayLoad = {
                limit: perPage,
                offset: pageNumber,
                filterStatus: status,
                order: tableOrder,
                orderId: tableOrderBy,
                searchQuery: searchValue,
                treatment: treatmentData?.toString(),
                startDate,
                endDate,
                contactedCount: contactCount,
                tags: searchTagsValue.toString(),
                referral,
                depositPendingStatus,
                category,
                bookedWith
            };
            handleStartedTablePayload(reloadPayLoad);
            updateLeadPage(reloadPayLoad);
            if (reloadCounter) {
                reloadCounter(reloadPayLoad);
            }
        }
    }, [searchTagsValue]);

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
        } else if (position && listElem.current) {
            listElem.current.scrollTop = position - listElem?.current?.offsetHeight;
        }
    }, [position]);

    const defaultValues = {
        tags: []
    };

    const methods = useForm({
        mode: 'onChange',
        resolver: yupResolver(AutocompleteTagsSchema),
        defaultValues,
    });

    const {
        setValue,
        control,
    } = methods;

    const handleDelete = (chipToDelete) => {
        const tags = searchTags?.filter(value => value.id !== chipToDelete.id);
        const value = searchTagsValue?.filter(value => value !== chipToDelete.name);
        handlePage();
        setShouldCall(true);
        UpdateTableData(tableType, { tag: tags, filterTags: value });
    };

    const handleSearch = (data) => {
        if (data?.isNoTags) {
            setShouldCall(true);
            handlePage();
            UpdateTableData(tableType, { tag: [NO_TAGS_OPTION], filterTags: ['__NO_TAGS__'], perPage: 10, pageNumber: 0 });
            return;
        }
        const tags = searchTags?.filter(value => value?.id === data?.id);
        if (data?.id > 0 && tags?.length <= 0) {
            // If "No Tags" was previously selected, clear it before adding a regular tag
            const currentTags = searchTags?.filter(t => !t.isNoTags) || [];
            const currentFilterTags = searchTagsValue?.filter(t => t !== '__NO_TAGS__') || [];
            setShouldCall(true);
            handlePage();
            switch (tableType) {
                case 'NewLeadTable':
                    UpdateTableData(tableType, { tag: [...currentTags.filter(t => newTag.includes(t)), data], filterTags: [...currentFilterTags.filter(t => newFilterTags.includes(t)), data?.name], perPage: 10, pageNumber: 0 });
                    return;
                case 'ConsultantAttend':
                    UpdateTableData(tableType, { tag: [...attendedTag.filter(t => !t.isNoTags), data], filterTags: [...attendedFilterTags.filter(t => t !== '__NO_TAGS__'), data?.name], perPage: 10, pageNumber: 0 });
                    return;
                case 'AttendedChecklists':
                    UpdateTableData(tableType, { tag: [...attendedChecklistsTag.filter(t => !t.isNoTags), data], filterTags: [...attendedChecklistsFilterTags.filter(t => t !== '__NO_TAGS__'), data?.name], perPage: 10, pageNumber: 0 });
                    return;
                case 'ConsultantClinCheckAttend':
                    UpdateTableData(tableType, { tag: [...clincheckAttendedTag.filter(t => !t.isNoTags), data], filterTags: [...clincheckAttendedFilterTags.filter(t => t !== '__NO_TAGS__'), data?.name], perPage: 10, pageNumber: 0 });
                    return;
                case 'ConsultantBooked':
                    UpdateTableData(tableType, { tag: [...bookedTag.filter(t => !t.isNoTags), data], filterTags: [...bookedFilterTags.filter(t => t !== '__NO_TAGS__'), data?.name], perPage: 10, pageNumber: 0 });
                    return;
                case 'treatmentStarted':
                    UpdateTableData(tableType, { tag: [...treatmentTag.filter(t => !t.isNoTags), data], filterTags: [...treatmentFilterTags.filter(t => t !== '__NO_TAGS__'), data?.name], perPage: 10, pageNumber: 0 });
                    return;
                case 'DashNewLeadTable':
                    UpdateTableData(tableType, { tag: [...dashNewTag.filter(t => !t.isNoTags), data], filterTags: [...dashNewFilterTags.filter(t => t !== '__NO_TAGS__'), data?.name], perPage: 10, pageNumber: 0 });
                    return;
                case 'ArchiveTable':
                    UpdateTableData(tableType, { tag: [...archivedContactTags.filter(t => !t.isNoTags), data], filterTags: [...archiveFilterTags.filter(t => t !== '__NO_TAGS__'), data?.name], perPage: 10, pageNumber: 0 });
                    return;
                default:
                    UpdateTableData(tableType, { tag: [...dashTag.filter(t => !t.isNoTags), data], filterTags: [...dashFilterTags.filter(t => t !== '__NO_TAGS__'), data?.name], perPage: 10, pageNumber: 0 });
                    break;
            }
        }
    };

    const handleTags = (event, newValue) => {

        if (event.key !== 'Backspace') {
            const lastItem = newValue?.[newValue.length - 1];
            if (lastItem?.isNoTags) {
                // "No Tags" selected — clear regular tags, set only "No Tags"
                handleSearch(NO_TAGS_OPTION);
            } else if (lastItem?.inputValue) {
                handleSearch({});
                setValue('tagName', '');
            }
            else {
                setValue('tags', lastItem?.name);
                handleSearch(lastItem);
            }
        }
    };

    const optionLabel = (option) => {
        if (typeof option === 'string') {
            return option?.name;
        }
        if (option?.inputValue) {
            return option?.inputValue;
        }
        return option?.name;
    };

    const leadMoreData = () => {
        if (count > results?.length) {
            dispatch(getAllTagItem(practiceId, 30, results?.length));
        }
    };

    const leadMorePosition = (event) => {
        const listboxNode = event?.target?.scrollHeight <= event?.target?.scrollTop + event?.target?.clientHeight;
        if (listboxNode) {
            setPosition(event?.target?.scrollTop + event?.target?.clientHeight);
            leadMoreData();
        }
    };

    useEffect(() => {
        if (practiceId) {
            if (inputValueDebounce) {
                const data = {
                    tagged__items__tag__name__icontains: inputValueDebounce,
                };
                dispatch(getAllTagItem(practiceId, 30, 0, data));
            } else if (needToCall.current) {
                needToCall.current = false;
                dispatch(getAllTagItem(practiceId, 30, 0));
            }
        }
    }, [inputValueDebounce]);


    const inputChangeHandler = (value) => {
        dispatch(startLoading());
        setInputValue(value);
        if (isEmpty(value)) {
            needToCall.current = true;
        }
    };

    return (
        <Box sx={tagsBoxStyle}>
            <Controller
                name="tags"
                control={control}
                render={() => (
                    <Box sx={{ width: '100%' }}>
                        <Autocomplete
                            multiple
                            value={searchTags || []}
                            onChange={handleTags}
                            onInputChange={(e, newValue) => inputChangeHandler(newValue)}
                            id="free-solo-dialog-demo"
                            options={tagsOptions}
                            filterOptions={(options) => options}
                            noOptionsText={isLoading ? 'Loading...' : 'No options'}
                            getOptionLabel={optionLabel}
                            selectOnFocus
                            handleHomeEndKeys
                            renderOption={(props, option) => (
                                <li {...props} key={option?.name} style={{ padding: '4px 9px', ...(option?.isNoTags ? { borderBottom: '1px solid #e2e8f0' } : {}) }}>
                                    <Chip
                                        size='small'
                                        label={option?.name}
                                        sx={{
                                            height: '26px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            ...(option?.isNoTags ? {
                                                bgcolor: 'rgba(255, 86, 48, 0.08)',
                                                color: '#B71D18',
                                                border: '1px solid rgba(255, 86, 48, 0.24)',
                                                fontStyle: 'italic',
                                            } : option?.colour ? {
                                                bgcolor: `${option.colour}22`,
                                                color: option.colour,
                                                border: `1px solid ${option.colour}44`,
                                            } : {
                                                bgcolor: 'rgba(145, 158, 171, 0.12)',
                                                color: 'text.secondary',
                                            }),
                                            '& .MuiChip-label': {
                                                px: '8px',
                                            },
                                        }}
                                    />
                                </li>
                            )}
                            ListboxProps={{
                                ref: listElem,
                                onScroll: leadMorePosition
                            }}
                            isOptionEqualToValue={(option, val) => option?.id === val?.id}
                            getOptionDisabled={(option) => option?.isNoTags && searchTags?.some(t => t.isNoTags)}
                            renderTags={(value) =>
                                value.map((option) => (
                                    <Chip
                                        sx={option?.isNoTags ? { bgcolor: 'rgba(255, 86, 48, 0.08)', color: '#B71D18', border: '1px solid rgba(255, 86, 48, 0.24)', fontStyle: 'italic', '& .MuiChip-deleteIcon': { color: '#B71D18' } } : InputTags(option?.colour)}
                                        key={option?.id}
                                        label={option?.name}
                                        size="small"
                                        color={option?.isNoTags ? 'error' : 'info'}
                                        variant="soft"
                                        onDelete={() => handleDelete(option)}
                                    />
                                ))
                            }
                            sx={searchTagsStyle('')}
                            slotProps={{ paper: { sx: filterDropdownPaperStyle } }}
                            renderInput={(params) => <TextField {...params} label="Tags" sx={{ 'label': { background: grey[200] } }} />}
                        />
                    </Box>
                )}
            />
        </Box>
    );
};

export default SearchAutocompleteTags;

SearchAutocompleteTags.propTypes = {
    reloadCounter: PropTypes.func,
    tableType: PropTypes.string,
    updateLeadPage: PropTypes.func,
    handlePage: PropTypes.func,
    handleStartedTablePayload: PropTypes.func
};